'use client';
import React, { useEffect, useRef } from 'react';

interface MemberData {
  name: string;
  id: string;
  status: 'Active' | 'Inactive';
  bio: string;
  phone?: string;
  profilePictureUrl?: string;
}

interface MembershipCardProps {
  member: MemberData;
}

export default function MembershipCard({ member }: MembershipCardProps) {
  const cardRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const t = setTimeout(() => {
      if (cardRef.current) {
        cardRef.current.style.opacity = '1';
        cardRef.current.style.transform = 'translateY(0)';
      }
    }, 50);
    return () => clearTimeout(t);
  }, []);

  const isActive = member.status === 'Active';
  const initials = member.name.split(' ').map(n => n[0]).join('');

  return (
    <div
      ref={cardRef}
      style={{
        opacity: 0,
        transform: 'translateY(32px)',
        transition: 'opacity 0.7s cubic-bezier(0.16,1,0.3,1), transform 0.7s cubic-bezier(0.16,1,0.3,1)',
      }}
    >
      <div
        className="relative rounded-3xl overflow-hidden"
        style={{
          background: 'linear-gradient(135deg, #111124 0%, #1A1A2E 50%, #0F0F1A 100%)',
          border: '1px solid rgba(123,47,247,0.35)',
          boxShadow: '0 0 60px 20px rgba(123,47,247,0.18), 0 0 120px 40px rgba(123,47,247,0.08)',
        }}
      >
        {/* Card shimmer line */}
        <div
          className="absolute top-0 left-0 right-0 h-[1px]"
          style={{ background: 'linear-gradient(90deg, transparent, rgba(123,47,247,0.6), transparent)' }}
        />

        {/* Radial glow inside card */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{ background: 'radial-gradient(ellipse 80% 60% at 50% 0%, rgba(123,47,247,0.12) 0%, transparent 70%)' }}
        />

        {/* ── TOP SECTION ── */}
        <div className="relative z-10 px-8 pt-8 pb-6 border-b border-[rgba(240,230,211,0.07)]">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-[10px] font-bold tracking-[0.25em] uppercase mb-1" style={{ color: 'rgba(240,230,211,0.4)' }}>
                Official Member Card
              </p>
              <h2 className="font-serif font-bold text-2xl" style={{ color: 'var(--cream)' }}>
                Conect Club
              </h2>
              <p className="text-[12px] font-light italic mt-0.5" style={{ color: 'rgba(240,230,211,0.5)' }}>
                Connect with a Purpose.
              </p>
            </div>
            {/* CC Logo mark */}
            <div
              className="w-12 h-12 rounded-xl flex items-center justify-center font-serif font-bold text-lg"
              style={{ background: 'rgba(123,47,247,0.2)', border: '1px solid rgba(123,47,247,0.4)', color: '#7B2FF7' }}
            >
              CC
            </div>
          </div>
        </div>

        {/* ── MIDDLE SECTION ── */}
        <div className="relative z-10 px-8 py-6 border-b border-[rgba(240,230,211,0.07)]">
          <div className="flex items-center gap-4 mb-5">
            {/* Avatar — photo or initials fallback */}
            <div
              className="w-16 h-16 rounded-2xl flex-shrink-0 overflow-hidden flex items-center justify-center font-serif font-bold text-xl"
              style={{ background: 'rgba(123,47,247,0.15)', border: '1px solid rgba(123,47,247,0.3)', color: '#7B2FF7' }}
            >
              {member.profilePictureUrl ? (
                <img
                  src={member.profilePictureUrl}
                  alt={`${member.name} profile photo`}
                  className="w-full h-full object-cover"
                />
              ) : (
                initials
              )}
            </div>
            <div>
              <h3 className="font-serif font-semibold text-xl" style={{ color: 'var(--cream)' }}>
                {member.name}
              </h3>
              <p className="text-[12px] font-mono mt-0.5" style={{ color: 'rgba(240,230,211,0.5)' }}>
                {member.id}
              </p>
              {/* Phone number */}
              {member.phone && (
                <div className="flex items-center gap-1.5 mt-1">
                  <svg width="11" height="11" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" style={{ color: 'rgba(240,230,211,0.4)', flexShrink: 0 }}>
                    <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07A19.5 19.5 0 013.07 9.81a19.79 19.79 0 01-3.07-8.68A2 2 0 012 .84h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L6.09 8.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 16.92z" />
                  </svg>
                  <p className="text-[11px]" style={{ color: 'rgba(240,230,211,0.45)' }}>
                    {member.phone}
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Status badge */}
          <div className="flex items-center gap-3 mb-5">
            <div
              className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full"
              style={{
                background: isActive ? 'rgba(74,222,128,0.1)' : 'rgba(239,68,68,0.1)',
                border: `1px solid ${isActive ? 'rgba(74,222,128,0.3)' : 'rgba(239,68,68,0.3)'}`,
              }}
            >
              <span
                className="w-2 h-2 rounded-full"
                style={{ background: isActive ? '#4ade80' : '#ef4444', boxShadow: `0 0 6px ${isActive ? '#4ade80' : '#ef4444'}` }}
              />
              <span
                className="text-[11px] font-bold tracking-[0.1em] uppercase"
                style={{ color: isActive ? '#4ade80' : '#ef4444' }}
              >
                {member.status}
              </span>
            </div>
            <span className="text-[11px]" style={{ color: 'rgba(240,230,211,0.3)' }}>
              Membership Status
            </span>
          </div>

          {/* Bio */}
          <div
            className="rounded-xl px-4 py-3"
            style={{ background: 'rgba(240,230,211,0.03)', border: '1px solid rgba(240,230,211,0.06)' }}
          >
            <p className="text-[11px] font-medium tracking-[0.1em] uppercase mb-1" style={{ color: 'rgba(240,230,211,0.35)' }}>
              Bio
            </p>
            <p className="text-sm leading-relaxed" style={{ color: 'rgba(240,230,211,0.65)' }}>
              &quot;{member.bio}&quot;
            </p>
          </div>
        </div>

        {/* ── BOTTOM SECTION ── */}
        <div className="relative z-10 px-8 py-6">
          <div className="flex items-center justify-between gap-6">
            {/* QR Code placeholder */}
            <div className="flex flex-col items-center gap-2">
              <div
                className="w-20 h-20 rounded-xl qr-shimmer flex items-center justify-center"
                style={{ border: '1px solid rgba(123,47,247,0.2)' }}
              >
                <svg width="52" height="52" viewBox="0 0 52 52" fill="none">
                  <rect x="2" y="2" width="20" height="20" rx="2" fill="rgba(123,47,247,0.4)" />
                  <rect x="6" y="6" width="12" height="12" rx="1" fill="rgba(123,47,247,0.6)" />
                  <rect x="30" y="2" width="20" height="20" rx="2" fill="rgba(123,47,247,0.4)" />
                  <rect x="34" y="6" width="12" height="12" rx="1" fill="rgba(123,47,247,0.6)" />
                  <rect x="2" y="30" width="20" height="20" rx="2" fill="rgba(123,47,247,0.4)" />
                  <rect x="6" y="34" width="12" height="12" rx="1" fill="rgba(123,47,247,0.6)" />
                  <rect x="30" y="30" width="4" height="4" rx="0.5" fill="rgba(123,47,247,0.5)" />
                  <rect x="36" y="30" width="4" height="4" rx="0.5" fill="rgba(123,47,247,0.5)" />
                  <rect x="42" y="30" width="4" height="4" rx="0.5" fill="rgba(123,47,247,0.5)" />
                  <rect x="30" y="36" width="4" height="4" rx="0.5" fill="rgba(123,47,247,0.5)" />
                  <rect x="42" y="36" width="4" height="4" rx="0.5" fill="rgba(123,47,247,0.5)" />
                  <rect x="30" y="42" width="4" height="4" rx="0.5" fill="rgba(123,47,247,0.5)" />
                  <rect x="36" y="42" width="4" height="4" rx="0.5" fill="rgba(123,47,247,0.5)" />
                  <rect x="42" y="42" width="4" height="4" rx="0.5" fill="rgba(123,47,247,0.5)" />
                </svg>
              </div>
              <p className="text-[9px] font-medium tracking-[0.08em] uppercase" style={{ color: 'rgba(240,230,211,0.3)' }}>
                Scan to verify
              </p>
            </div>

            {/* Verified badge */}
            <div className="flex-1 text-right">
              <div
                className="inline-flex items-center gap-2 px-4 py-2 rounded-xl mb-2"
                style={{ background: 'rgba(123,47,247,0.12)', border: '1px solid rgba(123,47,247,0.3)' }}
              >
                <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24" style={{ color: '#7B2FF7' }}>
                  <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span className="text-[11px] font-bold tracking-[0.08em]" style={{ color: '#7B2FF7' }}>
                  Verified Conect Member
                </span>
              </div>
              <p className="text-[10px]" style={{ color: 'rgba(240,230,211,0.25)' }}>
                conectclub.app
              </p>
            </div>
          </div>
        </div>

        {/* Bottom shimmer line */}
        <div
          className="absolute bottom-0 left-0 right-0 h-[1px]"
          style={{ background: 'linear-gradient(90deg, transparent, rgba(123,47,247,0.4), transparent)' }}
        />
      </div>
    </div>
  );
}
